import { Flame, X } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { BadgeImage } from "@/components/badges/badge-image"
import { BadgeSheetGrabber } from "@/components/badges/badge-sheet-grabber"
import { PremiumBadge } from "@/components/badges/premium-badge"
import { RarityPill } from "@/components/badges/rarity-pill"
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer"
import { Progress } from "@/components/ui/progress"
import type { Tier } from "@/lib/badge-tiers"
import { cn } from "@/lib/utils"
import type { BadgeRarity } from "@/types/badges"

/**
 * OKS-527 — §14 badge-detail bottom sheet (`ui/drawer`, Base UI since OKS-1022).
 *
 * Fully client-side — it reads a resolved `BadgeDetailData` view model
 * built by the page from the already-loaded Inertia props (TRD KD-2: no
 * second fetch, no new endpoint/policy). Renders the large 124px
 * medallion (with a progress **arc** overlay for a locked, threshold-
 * bearing badge), name, "category · rarity", how-to-earn, an optional
 * "your progress" block, and a pinned footer "🔥 N crewmates have this".
 *
 * ## Pinned footer (memory `crew_vaul_sheet_peek_footer_pinning`)
 *
 * The inner column's height tracks the VISIBLE slice of the active detent and
 * the footer is a non-scrolling flex child at its bottom, so it stays visible
 * at the peek detent — while remaining INSIDE the dialog focus scope. A plain
 * `sticky bottom-0` would pin to the full panel and sit off-screen at peek.
 *
 * The drawer manages focus (moves onto the sheet on open, returns to the
 * triggering tile on close) + Esc/backdrop/swipe dismissal (UX Spec §6). The
 * active detent lives in React state only — never localStorage (Crew rule).
 */
export interface BadgeDetailData {
  kind: "milestone" | "custom"
  state: "earned" | "locked"
  name: string
  icon: string
  accentColor: string
  imageUrl?: string | null
  tier: Tier
  /** Already-localised category label (e.g. "Streaks"). */
  categoryLabel: string
  rarity: BadgeRarity
  /** Already-localised how-to-earn sentence. */
  howToEarn: string
  holderCount: number
  /** Locked, threshold-bearing badges only. */
  progress?: { current: number; threshold: number } | null
  /** Earned badges — already-localised "Earned on {date}". */
  earnedAtLabel?: string | null
  /** Locked, threshold-bearing — already-localised "X to go". */
  toGoLabel?: string | null
}

/** Nominal detents: peek ~60 %, expanded ~95 % (UX Spec §3). */
const SNAP_POINTS = [0.6, 0.95] as const

/**
 * OKS-568 — cap the content panel at 92dvh so a translucent, blurred backdrop
 * band stays visible above it even at the tallest detent, instead of the
 * opaque panel painting over the whole viewport. Keep in sync with the
 * `h-[92dvh]` / `max-h-[92dvh]` classes on the sheet below.
 */
const SHEET_MAX_DVH = 92
const BACKDROP_BAND_DVH = 100 - SHEET_MAX_DVH

/**
 * OKS-1022 (TRD KD8) — the snap points handed to Base UI, in VISIBLE height.
 *
 * vaul (until OKS-1022) translated the 92dvh panel by `(1 − snap) × vh`, so
 * the user saw `100·snap − 8` dvh: 52 at peek, 87 expanded (13dvh band).
 * Base UI reads a snap point ≤ 1 as the visible height itself
 * (`useDrawerSnapPoints.resolveSnapPointValue`), so passing the nominal
 * 0.6/0.95 would make the sheet visibly taller. Subtracting the band keeps
 * today's heights exactly. Rounded to 2 decimals so the values compare by
 * identity (0.95 − 0.08 is 0.8699999… in floating point).
 */
const DETENTS = SNAP_POINTS.map(
  (nominal) => Math.round(nominal * 100 - BACKDROP_BAND_DVH) / 100,
)
const PEEK = DETENTS[0]
const EXPANDED = DETENTS[1]

/** Decorative SVG ring around the medallion showing the locked fraction. */
function ProgressArc({ fraction }: { fraction: number }) {
  const size = 132
  const stroke = 5
  const radius = (size - stroke) / 2
  const circumference = 2 * Math.PI * radius
  const clamped = Math.max(0, Math.min(1, fraction))

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      className="pointer-events-none absolute inset-0 h-full w-full -rotate-90"
      aria-hidden="true"
      data-testid="badge-detail-arc"
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        strokeWidth={stroke}
        className="stroke-muted"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - clamped)}
        className="stroke-primary motion-safe:transition-[stroke-dashoffset] motion-safe:duration-500"
      />
    </svg>
  )
}

export function BadgeDetailSheet({
  badge,
  onClose,
}: {
  badge: BadgeDetailData | null
  onClose: () => void
}) {
  const { t, i18n } = useTranslation()
  const [snap, setSnap] = useState<number | string | null>(PEEK)

  const open = badge != null
  const visibleFraction = typeof snap === "number" ? snap : PEEK
  const expanded = visibleFraction === EXPANDED
  // The inner column is exactly the visible slice, so its bottom (the pinned
  // footer) sits on the viewport's bottom edge at either detent.
  const visibleHeight = `${Math.round(visibleFraction * 100)}dvh`

  const locked = badge?.state === "locked"
  const progress = badge?.progress
  const fraction =
    progress && progress.threshold > 0
      ? progress.current / progress.threshold
      : 0

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onClose()
          setSnap(PEEK)
        }
      }}
      snapPoints={DETENTS}
      snapPoint={snap}
      onSnapPointChange={setSnap}
    >
      {/* `ui/drawer` renders the `drawer-overlay` backdrop (OKS-568 blur rule),
          dimmed at every detent. The geometry classes carry the same
          `data-[swipe-direction=down]:` variant as the primitive's defaults so
          they win the cascade over its generic `max-h-[80vh]` / `mt-24` /
          `rounded-t-lg`. */}
      <DrawerContent
        data-testid="badge-detail-sheet"
        // The chrome row (drag cue + ×) goes in `handle`, outside the drawer's
        // content wrapper, so a mouse press on the cue still starts a swipe.
        handle={
          <>
            {/* OKS-639 — top padding respects the iOS Safari top safe-area inset
                  (parity with the mission sheet's identical overlaid-close chrome);
                  `max(0.75rem, …)` keeps today's spacing on zero-inset devices. */}
            <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex items-start justify-between px-3 pt-[max(0.75rem,env(safe-area-inset-top))] pb-3">
              <BadgeSheetGrabber
                expanded={expanded}
                onToggle={() => setSnap(expanded ? PEEK : EXPANDED)}
                label={t("badges.detail.grabber")}
                // `mx-auto`: vaul's injected `[data-vaul-handle]` CSS gave the old
                // handle `margin: 0 auto`, which — inside this `justify-between`
                // row — centred it in the space left of ×. Restores that position.
                className="pointer-events-auto mx-auto"
              />
              <button
                type="button"
                onClick={onClose}
                data-testid="badge-detail-close"
                aria-label={t("badges.detail.close")}
                className="bg-muted text-muted-foreground hover:bg-muted/80 pointer-events-auto grid size-11 place-items-center rounded-full transition"
              >
                <X className="size-5" aria-hidden="true" />
              </button>
            </div>
          </>
        }
        className={cn(
          "mx-auto h-[92dvh] w-full",
          "data-[swipe-direction=down]:mt-0 data-[swipe-direction=down]:max-h-[92dvh] data-[swipe-direction=down]:rounded-t-2xl data-[swipe-direction=down]:border-t-0",
          "sm:max-w-[480px]",
        )}
      >
        <DrawerTitle className="sr-only">{badge?.name ?? ""}</DrawerTitle>

        <div
          className="flex min-h-0 flex-col"
          style={{ height: visibleHeight }}
        >
          <div
            data-base-ui-swipe-ignore
            className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 pt-12 pb-4"
          >
            {badge && (
              <div className="flex flex-col items-center gap-4 text-center">
                {/* Medallion + optional progress arc. */}
                <div className="relative grid size-[132px] place-items-center">
                  {locked && progress && <ProgressArc fraction={fraction} />}
                  {/* OKS-538/573 — custom → frame-less image always; a
                        milestone → image only when a brand override is
                        present, else the Lucide medallion. */}
                  {badge.kind === "custom" || badge.imageUrl != null ? (
                    <BadgeImage
                      imageUrl={badge.imageUrl}
                      accentColor={badge.accentColor}
                      name={badge.name}
                      size="detail"
                      state={badge.state}
                      decorative
                    />
                  ) : (
                    <PremiumBadge
                      tier={badge.tier}
                      icon={badge.icon}
                      state={badge.state}
                      size="detail"
                    />
                  )}
                </div>

                <h3 className="line-clamp-2 text-xl font-bold">{badge.name}</h3>

                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground text-sm capitalize">
                    {badge.categoryLabel}
                  </span>
                  <RarityPill rarity={badge.rarity} />
                </div>

                <div className="w-full text-left">
                  <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                    {t("badges.detail.how_to_earn")}
                  </p>
                  <p className="mt-1 text-sm">{badge.howToEarn}</p>
                </div>

                {/* Your-progress block. */}
                {badge.state === "earned" && badge.earnedAtLabel && (
                  <div
                    className="w-full text-left"
                    data-testid="badge-detail-earned"
                  >
                    <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                      {t("badges.detail.your_progress")}
                    </p>
                    <p className="mt-1 text-sm">{badge.earnedAtLabel}</p>
                  </div>
                )}

                {locked && progress && (
                  <div
                    className="flex w-full flex-col gap-1.5 text-left"
                    data-testid="badge-detail-progress"
                  >
                    <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                      {t("badges.detail.your_progress")}
                    </p>
                    <Progress
                      value={Math.round(fraction * 100)}
                      aria-label={badge.toGoLabel ?? undefined}
                    />
                    <p className="text-muted-foreground text-sm">
                      {/* OKS-558 — thousands separators so views tiles
                            read "6,200 / 10,000", locale-aware (FR uses a
                            narrow space). Small counts are unaffected. */}
                      {progress.current.toLocaleString(i18n.language)}/
                      {progress.threshold.toLocaleString(i18n.language)}
                      {badge.toGoLabel ? ` · ${badge.toGoLabel}` : ""}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Pinned footer — visible at every detent, peek included. */}
          {badge && (
            <div
              data-testid="badge-detail-footer"
              className="bg-background flex shrink-0 items-center justify-center gap-1.5 border-t p-4"
            >
              <Flame
                className="size-4 shrink-0 text-orange-500"
                aria-hidden="true"
              />
              <span className="text-sm font-medium">
                {badge.holderCount > 0
                  ? t("badges.detail.crewmates_have", {
                      count: badge.holderCount,
                    })
                  : t("badges.detail.be_first")}
              </span>
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  )
}
